if (!customElements.get('product-page')) {
  customElements.define(
    'product-page',
    class ProductPage extends HTMLElement {
      connectedCallback() {
        const inventory = JSON.parse(this.querySelector('.variants-inventory').textContent);
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent)
          .filter((variant) => variant.id in inventory)
          .map((variant) => ({ ...variant, available: inventory[variant.id] }));
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
        this.live = this.querySelector('[data-live]');
        this.renderToken = 0;

        this.abort = new AbortController();
        const options = { signal: this.abort.signal };
        document.addEventListener('change', this.onChange.bind(this), options);
        document.addEventListener('submit', this.onSubmit.bind(this), options);
        document.addEventListener('click', this.onClick.bind(this), options);
        window.addEventListener('resize', this.updateStickyTop.bind(this), options);

        this.initMedia();
        this.updateStickyTop();
        document.querySelector('sticky-buy-bar')?.observe?.();
      }

      disconnectedCallback() {
        this.abort?.abort();
      }

      get alternatives() {
        return this.querySelector('[data-alternatives]');
      }

      onClick(event) {
        const alternativesOpener = event.target.closest('[data-open-alternatives]');
        if (alternativesOpener) {
          this.openAlternatives(alternativesOpener);
          return;
        }

        const scrollLink = event.target.closest('[data-scroll-target]');
        if (scrollLink && this.contains(scrollLink)) {
          const target = document.getElementById(scrollLink.dataset.scrollTarget);
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: this.motion, block: 'start' });
          target.querySelector('a, button, [tabindex]')?.focus({ preventScroll: true });
          return;
        }

        if (!this.contains(event.target)) return;

        const mediaOpener = event.target.closest('[data-open-media]');
        if (mediaOpener) {
          this.openMedia(Number(mediaOpener.dataset.openMedia), mediaOpener);
        }
      }

      get motion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      onChange(event) {
        const input = event.target.closest('[data-option-value]');
        if (!input || input.closest('featured-product-variants')) return;
        const container = input.closest('.product__options');
        const values = [...container.querySelectorAll('[data-option-position]')].map(
          (fieldset) => fieldset.querySelector('[data-option-value]:checked')?.value
        );
        const variant = this.variants.find((item) => item.options.every((option, index) => option === values[index]));
        if (variant) this.renderVariant(variant, input.id);
      }

      async renderVariant(variant, focusId) {
        const token = ++this.renderToken;
        const bar = document.querySelector('sticky-buy-bar');
        const sections = [this.dataset.section, bar?.dataset.section].filter(Boolean);
        const url = `${this.dataset.url}?variant=${variant.id}`;

        try {
          const response = await fetch(`${url}&sections=${sections.join(',')}`);
          const data = await response.json();
          if (token !== this.renderToken) return;

          const page = new DOMParser().parseFromString(data[this.dataset.section], 'text/html');
          const rating = this.querySelector('product-rating');
          ['media', 'info', 'specs'].forEach((region) => {
            const next = page.querySelector(`[data-region="${region}"]`);
            if (next) this.querySelector(`[data-region="${region}"]`)?.replaceWith(next);
          });
          if (rating) this.querySelector('product-rating')?.replaceWith(rating);
          window.Shopify?.PaymentButton?.init();
          if (bar) bar.update(data[bar.dataset.section]);

          window.history.replaceState({}, '', url);
          this.initMedia();
          this.updateStickyTop();
          document.getElementById(focusId)?.focus({ preventScroll: true });

          const price = this.querySelector('[data-price] .price-item--last, [data-price] .price-item--regular');
          if (price) this.live.textContent = price.textContent.trim();
        } catch (error) {
          console.error(error);
        }
      }

      onSubmit(event) {
        const form = event.target.closest('[data-product-form]');
        if (!form) return;
        event.preventDefault();
        this.addToCart(form);
      }

      async addToCart(form) {
        const button = form.querySelector('[data-add-button]');
        if (this.adding || button.getAttribute('aria-disabled') === 'true') return;
        this.adding = true;

        this.setMessage(form, null);
        button.classList.add('loading');
        button.setAttribute('aria-busy', 'true');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = new FormData(form);
        if (cart) {
          body.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          body.append('sections_url', window.location.pathname);
          cart.setActiveElement(form.closest('dialog')?.opener || document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await response.json();

          if (data.status) {
            await this.handleAddError(form, Number(body.get('id')));
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          form.closest('dialog')?.close();
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product__form', productVariantId: body.get('id'), cartData: data });
          }
          cart.renderContents(data);
        } catch (error) {
          console.error(error);
          this.setMessage(form, this.strings.addFailedRetry);
        } finally {
          this.adding = false;
          button.classList.remove('loading');
          button.removeAttribute('aria-busy');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      async handleAddError(form, variantId) {
        let available = true;
        try {
          const response = await fetch(`${this.dataset.url}.js`);
          const product = await response.json();
          available = product.variants.find((variant) => variant.id === variantId)?.available !== false;
        } catch (error) {
          console.error(error);
        }

        if (available) {
          this.setMessage(form, this.strings.addFailedRetry);
          return;
        }

        document.querySelectorAll('[data-product-form]').forEach((productForm) => {
          const button = productForm.querySelector('[data-add-button]');
          button.setAttribute('aria-disabled', 'true');
          button.querySelector('[data-add-label]').textContent = this.strings.soldOut;
          productForm.querySelector('[data-express]')?.remove();
          this.setMessage(productForm, this.strings.addFailedSold);
        });

        const inventory = this.querySelector('[data-inventory]');
        if (inventory) {
          inventory.className = 'product__stock product__stock--sold-out';
          inventory.textContent = this.strings.soldOut;
        }
        this.querySelector('[data-add-failed]')?.removeAttribute('hidden');
        document.querySelector('.product__strap')?.remove();
      }

      setMessage(form, text) {
        const message = form.querySelector('[data-add-message]');
        if (!message) return;
        message.hidden = !text;
        message.querySelector('[data-add-message-text]').textContent = text || '';
      }

      async openAlternatives(opener) {
        const dialog = this.alternatives;
        if (!dialog) return;
        document.querySelector('[data-buy-sheet][open]')?.close();
        window.openSheet(dialog, opener);
        this.loadSimilar();
      }

      loadSimilar() {
        window.loadSimilarProducts(this.alternatives.querySelector('[data-similar]'));
      }

      initMedia() {
        this.gallery = this.querySelector('[data-gallery]');
        if (!this.gallery) return;

        this.gallery.querySelectorAll('.product__media-image[loading="lazy"]').forEach((image) => {
          if (image.complete) return;
          image.classList.add('is-pending');
          const reveal = () => image.classList.remove('is-pending');
          image.addEventListener('load', reveal, { once: true });
          image.addEventListener('error', reveal, { once: true });
        });

        this.gallery.addEventListener(
          'scroll',
          () => {
            const index = Math.round(this.gallery.scrollLeft / this.gallery.clientWidth);
            this.querySelectorAll('[data-dot]').forEach((dot) => {
              dot.classList.toggle('is-active', Number(dot.dataset.dot) === index);
            });
          },
          { passive: true }
        );
      }

      updateStickyTop() {
        const info = this.querySelector('.product__info--sticky');
        if (!info) return;
        const gap = 32;
        const top = Math.min(gap, window.innerHeight - info.offsetHeight - gap);
        this.style.setProperty('--product-info-top', `${top}px`);
      }

      openMedia(index, opener) {
        const modal = this.querySelector('[data-media-modal]');
        if (!modal) return;
        if (!modal.controller) modal.controller = new MediaModal(modal);
        this.querySelectorAll('.product__slide video').forEach((video) => (video.muted = true));
        modal.controller.open(index, opener);
      }
    }
  );
}

class MediaModal {
  constructor(dialog) {
    this.dialog = dialog;
    this.items = [...dialog.querySelectorAll('[data-modal-item]')];
    this.counter = dialog.querySelector('[data-counter]');
    this.zoomButtons = dialog.querySelectorAll('[data-zoom]');
    this.current = 0;
    this.level = 0;
    this.pan = { x: 0, y: 0 };
    this.lastTap = 0;
    this.fine = window.matchMedia('(pointer: fine)');

    dialog.addEventListener('click', this.onClick.bind(this));
    dialog.addEventListener('mousemove', this.onMouseMove.bind(this));
    dialog.addEventListener('keydown', this.onKeyDown.bind(this));
    dialog.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    dialog.addEventListener('close', this.onClose.bind(this));
    this.onPopState = () => dialog.open && dialog.close();
  }

  get item() {
    return this.items[this.current];
  }

  get image() {
    return this.item.querySelector('.product-modal__image');
  }

  get isImage() {
    return this.item.dataset.type === 'image';
  }

  get fullWidth() {
    return Number(this.item.dataset.fullWidth) || 0;
  }

  // The last level shows the original at full resolution
  get levels() {
    const full = this.image?.clientWidth && this.fullWidth ? this.fullWidth / this.image.clientWidth : 2;
    return full > 2 ? [1, 2, full] : [1, Math.max(full, 1.5)];
  }

  open(index, opener) {
    this.items.forEach((item) => (item.hidden = true));
    this.show(index);
    window.history.pushState({ media: true }, '', '#media');
    window.addEventListener('popstate', this.onPopState);
    // The modal covers the gallery, but its videos still count as in view
    this.covered = [...document.querySelectorAll('media-video')].filter(
      (element) => !this.dialog.contains(element) && !element.video?.paused
    );
    this.covered.forEach((element) => element.video.pause());
    window.openSheet(this.dialog, opener);
  }

  onClose() {
    this.pauseMedia();
    this.covered?.forEach((element) => element.play());
    this.covered = null;
    window.removeEventListener('popstate', this.onPopState);
    if (window.location.hash === '#media') window.history.back();
  }

  pauseMedia() {
    this.item.querySelectorAll('video').forEach((video) => video.pause());
    this.item
      .querySelectorAll('iframe')
      .forEach((frame) => frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
  }

  show(index) {
    this.pauseMedia();
    this.item.hidden = true;
    this.current = (index + this.items.length) % this.items.length;
    this.item.hidden = false;
    this.level = 0;
    this.pan = { x: 0, y: 0 };
    if (this.image) this.image.style.transformOrigin = '';
    this.counter.textContent = `${this.current + 1} / ${this.items.length}`;
    this.apply();
  }

  apply() {
    const levels = this.levels;
    this.item.classList.toggle('is-zoomed', this.level > 0);
    if (this.image) {
      if (this.level && this.fullWidth) this.image.sizes = `${this.fullWidth}px`;
      this.image.style.transform = this.level
        ? `translate(${this.pan.x}px, ${this.pan.y}px) scale(${levels[this.level]})`
        : '';
    }
    this.zoomButtons[0].disabled = !this.isImage || this.level === 0;
    this.zoomButtons[1].disabled = !this.isImage || this.level === levels.length - 1;
  }

  zoom(step, origin) {
    if (!this.isImage) return;
    this.level = Math.min(this.levels.length - 1, Math.max(0, this.level + step));
    if (!this.level) this.pan = { x: 0, y: 0 };
    if (origin) this.image.style.transformOrigin = origin;
    this.apply();
  }

  originFrom(event) {
    const rect = this.item.getBoundingClientRect();
    return `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
  }

  onClick(event) {
    const step = event.target.closest('[data-step]');
    const zoomButton = event.target.closest('[data-zoom]');
    if (step) this.show(this.current + Number(step.dataset.step));
    else if (zoomButton) this.zoom(Number(zoomButton.dataset.zoom));
    else if (event.target === this.image && this.fine.matches) {
      this.zoom(this.level ? -this.level : this.levels.length - 1, this.originFrom(event));
    }
  }

  onMouseMove(event) {
    if (this.level && this.fine.matches && event.target === this.image) {
      this.image.style.transformOrigin = this.originFrom(event);
    }
  }

  onKeyDown(event) {
    if (event.key === 'ArrowRight') this.show(this.current + 1);
    else if (event.key === 'ArrowLeft') this.show(this.current - 1);
    else if (event.key === '+' || event.key === '=') this.zoom(1);
    else if (event.key === '-') this.zoom(-1);
  }

  onTouchStart(event) {
    if (!this.isImage || !event.target.closest('.product-modal__item')) return;
    const item = this.item;
    const track = (onMove) => {
      const onEnd = () => {
        item.classList.remove('is-panning');
        item.removeEventListener('touchmove', onMove);
        item.removeEventListener('touchend', onEnd);
      };
      item.addEventListener('touchmove', onMove, { passive: true });
      item.addEventListener('touchend', onEnd);
    };

    if (event.touches.length === 2) {
      event.preventDefault();
      const distance = (touches) =>
        Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      const start = distance(event.touches);
      const startLevel = this.level;
      track((moveEvent) => {
        if (moveEvent.touches.length < 2) return;
        const ratio = distance(moveEvent.touches) / start;
        this.level = Math.min(this.levels.length - 1, Math.max(0, Math.round(startLevel + (ratio - 1) * 2)));
        if (!this.level) this.pan = { x: 0, y: 0 };
        this.apply();
      });
      return;
    }

    const now = Date.now();
    if (now - this.lastTap < 300) {
      event.preventDefault();
      this.zoom(this.level ? -this.level : 1, '50% 50%');
      this.lastTap = 0;
      return;
    }
    this.lastTap = now;
    if (!this.level) return;

    const startX = event.touches[0].clientX - this.pan.x;
    const startY = event.touches[0].clientY - this.pan.y;
    item.classList.add('is-panning');
    track((moveEvent) => {
      this.pan = { x: moveEvent.touches[0].clientX - startX, y: moveEvent.touches[0].clientY - startY };
      this.apply();
    });
  }
}

if (!customElements.get('product-rating')) {
  customElements.define(
    'product-rating',
    class ProductRating extends HTMLElement {
      async connectedCallback() {
        try {
          const params = new URLSearchParams({ store: this.dataset.store, sku: this.dataset.sku, per_page: 1 });
          const response = await fetch(`https://api.reviews.io/product/review?${params}`);
          const { stats } = await response.json();
          const count = Number(stats?.count);
          if (!count) return;

          const average = Number(stats.average);
          const value = average.toLocaleString(document.documentElement.lang || 'de', { maximumFractionDigits: 1 });
          const filled = Math.round(average);
          const star = this.querySelector('[data-star]').innerHTML;
          const starFill = this.querySelector('[data-star-fill]').innerHTML;
          const link = this.querySelector('[data-rating]');

          link.setAttribute('aria-label', this.dataset.label.replace('[rating]', value).replace('[count]', count));
          link.innerHTML = `<span class="rating__stars" aria-hidden="true">${[1, 2, 3, 4, 5]
            .map((index) => `<span class="svg-wrapper">${index <= filled ? starFill : star}</span>`)
            .join('')}</span><span class="rating__value" aria-hidden="true">${value}</span><span class="rating-count" aria-hidden="true">${this.dataset.countLabel.replace('[count]', count)}</span>`;
          this.hidden = false;
        } catch (error) {
          console.error(error);
        }
      }
    }
  );
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.desktop = window.matchMedia('(min-width: 750px)');
        this.addEventListener('click', this.onClick.bind(this));
        this.observe();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.documentElement.classList.remove('has-sticky-buy-bar');
      }

      observe() {
        const target = document.querySelector('product-page [data-buy-buttons]');
        if (!target) return;
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting));
        this.observer.observe(target);
      }

      toggle(visible) {
        this.classList.toggle('is-visible', visible);
        document.documentElement.classList.toggle('has-sticky-buy-bar', visible);
      }

      onClick(event) {
        const select = event.target.closest('[data-sticky-select]');
        if (!select) return;
        if (this.desktop.matches) {
          const box = document.querySelector('product-page [data-buy-box]');
          box?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
          box?.querySelector('.product__options a, .product__options input')?.focus({ preventScroll: true });
        } else {
          window.openSheet(this.querySelector('[data-buy-sheet]'), select);
        }
      }

      update(html) {
        const next = new DOMParser().parseFromString(html, 'text/html');
        ['bar', 'sheet'].forEach((region) => {
          const fresh = next.querySelector(`[data-region="${region}"]`);
          const current = this.querySelector(`[data-region="${region}"]`);
          if (fresh && current) current.replaceWith(fresh);
        });
        this.observe();
      }
    }
  );
}
